
import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { StageWrapper } from '@/features/enrollment/core/StageWrapper'
import type { ValidationErrors } from '@/features/enrollment'
import { Sparkles, AlertTriangle, Info, Star, Shield } from 'lucide-react'
import { STATE_INCOME_RANGES } from '../config/aca.config'
import { useAuth } from '@/features/auth/hooks/useAuth'

interface EmploymentPlanStepProps {
    enrollmentData: any
    updateEnrollmentData: (field: string, value: any) => void
    handleNavigate: (targetIndex: number) => void | Promise<void>
    handleNavigateWithCompletion?: (targetIndex: number) => Promise<void>
    isNavigating: boolean
    currentStepIndex?: number
    totalSteps?: number
    [key: string]: any
}

// 2026 Federal Poverty Level (FPL) guidelines
const FPL_2026: { [key: number]: number } = {
    1: 15060,
    2: 20440,
    3: 25820,
    4: 31200,
    5: 36580,
    6: 41960,
    7: 47340,
    8: 52720
}

const getFPL = (householdSize: number): number => {
    if (householdSize <= 8) {
        return FPL_2026[householdSize] || FPL_2026[1]
    }
    return FPL_2026[8] + (householdSize - 8) * 5380
}

// Type of work options for self-employed
const SELF_EMPLOYED_WORK_TYPES = [
    'Please Select',
    'Housekeeping',
    'Landscaping',
    'Consultant',
    'Gig Worker',
    'Administrative Assistant',
    'Customer Service',
    'Sales Representative',
    'Warehouse Worker',
    'Delivery Driver',
    'General Laborer'
]

export default function EmploymentPlanStep({
    enrollmentData,
    updateEnrollmentData,
    handleNavigate,
    handleNavigateWithCompletion,
    isNavigating,
    currentStepIndex,
    totalSteps = 6,
    isMasterMode = false,
    revealAllFields = false
}: EmploymentPlanStepProps) {
    const { user } = useAuth()
    const [errors, setErrors] = useState<ValidationErrors>({})

    // Get available carriers from verification step
    const availableCarriers = enrollmentData.availableCarriers || []

    // Fix/Sync household size logic to match EnrollmentDataStep
    useEffect(() => {
        let size = 1 // Client
        if (enrollmentData.maritalStatus === '3') {
            size += 1
        }
        if (enrollmentData.dependentsList && Array.isArray(enrollmentData.dependentsList)) {
            // ONLY count dependents who are on the policy
            size += enrollmentData.dependentsList.filter((d: any) => d.onPolicy === 'Yes').length
        }

        if (enrollmentData.householdSize !== size) {
            updateEnrollmentData('householdSize', size)
        }
    }, [enrollmentData.maritalStatus, enrollmentData.dependentsList, updateEnrollmentData, enrollmentData.householdSize])

    // Auto-suggest income based on state and household size
    const suggestIncome = useCallback(() => {
        const householdSize = enrollmentData.householdSize || 1
        const stateCode = enrollmentData.clientState || 'default'

        // Get income range for household size (use default if state-specific not available)
        const ranges = STATE_INCOME_RANGES[stateCode] || STATE_INCOME_RANGES['default']
        const rangeIndex = Math.min(householdSize - 1, ranges.length - 1)
        const range = ranges[rangeIndex]

        if (range) {
            // Generate random income within the range (biased toward middle for realism)
            const midPoint = (range.min + range.max) / 2
            const variance = (range.max - range.min) * 0.3 // 30% variance from midpoint
            const randomOffset = (Math.random() - 0.5) * 2 * variance
            const rawIncome = Math.round(midPoint + randomOffset)

            // Round last 2 digits to 00 (e.g., 38192 becomes 38100)
            const suggestedIncome = Math.floor(rawIncome / 100) * 100

            updateEnrollmentData('annualIncome', suggestedIncome.toString())
        }
    }, [enrollmentData.householdSize, enrollmentData.clientState, updateEnrollmentData])

    // Set default values for employment and income
    useEffect(() => {
        // Set default Employment Type to "Self-Employed"
        if (!enrollmentData.employmentType) {
            updateEnrollmentData('employmentType', 'Self-Employed')

            // Auto-select a random TYPE of WORK choice for self-employed
            const validWorkTypes = SELF_EMPLOYED_WORK_TYPES.filter(t => t !== 'Please Select')
            const randomWorkType = validWorkTypes[Math.floor(Math.random() * validWorkTypes.length)]
            updateEnrollmentData('typeOfWork', randomWorkType)
        }

        // Auto-generate income if not already set
        if (!enrollmentData.annualIncome || enrollmentData.annualIncome === '0') {
            suggestIncome()
        }

        // Auto-select preferred carrier if one exists and none is selected
        if (availableCarriers.length > 0 && !enrollmentData.selectedCarrier) {
            // First, try to find a carrier marked as preferred in FMO configuration
            const preferredCarrier = availableCarriers.find((c: any) => c.isPreferred)

            if (preferredCarrier) {
                console.log('✨ [EmploymentPlanStep] Auto-selecting FMO preferred carrier:', preferredCarrier.name)
                updateEnrollmentData('selectedCarrier', preferredCarrier.code)
                updateEnrollmentData('carrierDesired', preferredCarrier.name)
            } else {
                // If no preferred carrier configured, auto-select by highest paying commission
                const carriersWithCommission = availableCarriers.filter((c: any) => c.commissionRate !== null && c.commissionRate !== undefined)

                if (carriersWithCommission.length > 0) {
                    // Sort by commission rate (highest first) and select the top one
                    const highestCommissionCarrier = carriersWithCommission.sort((a: any, b: any) =>
                        parseFloat(b.commissionRate) - parseFloat(a.commissionRate)
                    )[0]

                    console.log('✨ [EmploymentPlanStep] Auto-selecting highest commission carrier:', highestCommissionCarrier.name, `($${highestCommissionCarrier.commissionRate})`)
                    updateEnrollmentData('selectedCarrier', highestCommissionCarrier.code)
                    updateEnrollmentData('carrierDesired', highestCommissionCarrier.name)
                } else {
                    // Fallback: select first available carrier if no commission data
                    const fallbackCarrier = availableCarriers[0]
                    console.log('✨ [EmploymentPlanStep] Auto-selecting fallback carrier (no commission data):', fallbackCarrier.name)
                    updateEnrollmentData('selectedCarrier', fallbackCarrier.code)
                    updateEnrollmentData('carrierDesired', fallbackCarrier.name)
                }
            }
        }
    }, [enrollmentData.employmentType, enrollmentData.annualIncome, suggestIncome, updateEnrollmentData, availableCarriers, enrollmentData.selectedCarrier])

    // Check for Medicaid eligibility warning (too low income)
    const medicaidWarning = useMemo(() => {
        const householdSize = enrollmentData.householdSize || 1
        const annualIncome = parseFloat(enrollmentData.annualIncome) || 0

        if (annualIncome === 0) return null

        const fpl = getFPL(householdSize)
        const medicaidThreshold = fpl * 1.38

        if (annualIncome < medicaidThreshold) {
            return {
                show: true,
                threshold: medicaidThreshold,
                fpl: fpl,
                percentage: Math.round((annualIncome / fpl) * 100)
            }
        }

        return null
    }, [enrollmentData.householdSize, enrollmentData.annualIncome])

    // Check for high income warning (too high for subsidies)
    const highIncomeWarning = useMemo(() => {
        const householdSize = enrollmentData.householdSize || 1
        const annualIncome = parseFloat(enrollmentData.annualIncome) || 0

        if (annualIncome === 0) return null

        const fpl = getFPL(householdSize)
        const subsidyThreshold = fpl * 4.0

        if (annualIncome > subsidyThreshold) {
            return {
                show: true,
                threshold: subsidyThreshold,
                fpl: fpl,
                percentage: Math.round((annualIncome / fpl) * 100)
            }
        }

        return null
    }, [enrollmentData.householdSize, enrollmentData.annualIncome])


    const handleNext = useCallback(async (): Promise<boolean> => {
        const validationErrors: ValidationErrors = {}

        if (!enrollmentData.employmentType) {
            validationErrors.employmentType = 'Employment type is required'
        }
        if (!enrollmentData.annualIncome) {
            validationErrors.annualIncome = 'Annual income is required'
        }

        // Validate type of work for self-employed
        if (enrollmentData.employmentType === 'Self-Employed' && !enrollmentData.typeOfWork) {
            validationErrors.typeOfWork = 'Type of work is required for self-employed'
        }

        // Validate preferred carrier
        if (!enrollmentData.selectedCarrier) {
            validationErrors.selectedCarrier = 'Preferred Carrier is required'
        }

        setErrors(validationErrors)
        return Object.keys(validationErrors).length === 0
    }, [enrollmentData])

    return (
        <StageWrapper
            currentStepIndex={currentStepIndex ?? 2}
            totalSteps={totalSteps}
            handleNavigate={handleNavigate}
            handleNavigateWithCompletion={handleNavigateWithCompletion}
            onNext={handleNext}
            isNavigating={isNavigating}
        >
            <div className="card">
                <div className="flex items-center space-x-3 mb-6">
                    <div className="w-12 h-12 bg-secondary-100 rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-secondary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                    </div>
                    <div>
                        <h3 className="text-xl font-semibold text-accent-900">Step 3: Employment & Plan</h3>
                        <p className="text-sm text-accent-600">Employment, income, and health plan selection.</p>
                    </div>
                </div>

                {/* Master Mode: Financial & Carrier Monitor */}
                {isMasterMode && (
                    <div className="mb-8 p-5 bg-amber-50 border-2 border-amber-200 rounded-xl shadow-inner animate-in fade-in slide-in-from-top-2 duration-300">
                        <div className="flex items-center gap-2 mb-4">
                            <Shield className="w-5 h-5 text-amber-700" />
                            <h4 className="text-sm font-black text-amber-900 uppercase tracking-tighter">Financial & Carrier Monitor (2026 FPL)</h4>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <div className="bg-white border border-amber-100 rounded-lg p-3">
                                    <p className="text-[10px] font-bold text-amber-600 uppercase mb-2">FPL Math Table</p>
                                    <div className="space-y-1.5">
                                        <div className="flex justify-between text-xs">
                                            <span className="text-amber-700">Household Size:</span>
                                            <span className="font-mono font-bold text-amber-900">{enrollmentData.householdSize || 1}</span>
                                        </div>
                                        <div className="flex justify-between text-xs">
                                            <span className="text-amber-700">100% FPL Threshold:</span>
                                            <span className="font-mono font-bold text-amber-900">${getFPL(enrollmentData.householdSize || 1).toLocaleString()}</span>
                                        </div>
                                        <div className="flex justify-between text-xs">
                                            <span className="text-amber-700">Annual Income:</span>
                                            <span className="font-mono font-bold text-amber-900">${parseFloat(enrollmentData.annualIncome || 0).toLocaleString()}</span>
                                        </div>
                                        <div className="pt-1 mt-1 border-t border-amber-50 flex justify-between text-xs">
                                            <span className="text-amber-700 font-bold italic">Percent of FPL:</span>
                                            <span className="font-mono font-black text-indigo-600">
                                                {Math.round((parseFloat(enrollmentData.annualIncome || 0) / getFPL(enrollmentData.householdSize || 1)) * 100)}%
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="p-3 bg-white rounded-lg border border-amber-100 text-xs">
                                    <p className="text-[10px] font-bold text-amber-600 uppercase mb-2">Calculated Thresholds</p>
                                    <div className="space-y-1.5">
                                        <div className="flex justify-between">
                                            <span className="text-amber-700">Medicaid (138%):</span>
                                            <span className="font-mono font-bold text-amber-900">${Math.round(getFPL(enrollmentData.householdSize || 1) * 1.38).toLocaleString()}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-amber-700">High Income (400%):</span>
                                            <span className="font-mono font-bold text-amber-900">${Math.round(getFPL(enrollmentData.householdSize || 1) * 4.0).toLocaleString()}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div className="p-3 bg-white rounded-lg border border-amber-100">
                                    <p className="text-[10px] font-bold text-amber-600 uppercase mb-2">Carrier Diagnostics</p>
                                    <div className="space-y-2">
                                        <div className="flex justify-between text-[11px]">
                                            <span className="text-amber-700">Available:</span>
                                            <span className="font-bold text-green-700">{availableCarriers.length} Carriers</span>
                                        </div>
                                        <div className="flex justify-between text-[11px]">
                                            <span className="text-amber-700">Selected Code:</span>
                                            <span className="font-mono font-bold text-blue-700">{enrollmentData.selectedCarrier || 'NONE'}</span>
                                        </div>
                                        <div className="mt-2 pt-2 border-t border-amber-50">
                                            <p className="text-[9px] text-amber-500 font-bold uppercase mb-1">State Affinity:</p>
                                            <p className="text-[10px] text-amber-900 leading-tight">
                                                Filtering for <strong className="text-amber-700">{enrollmentData.clientState || 'ANY'}</strong>.
                                                If pool is empty, check `campaign.carriers` for state restrictions.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="p-3 bg-indigo-50 border border-indigo-100 rounded-lg">
                                    <p className="text-[9px] font-bold text-indigo-600 uppercase mb-1 leading-none italic">Calibration Rule</p>
                                    <p className="text-[10px] text-indigo-700 leading-tight">
                                        Subsidies trigger above 138% and taper off at 400%.
                                        <strong> Commission Rule:</strong> Carriers are ranked by raw payout unless FMO Preferred overrides.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="space-y-8">
                    {/* SECTION: Employment Information */}
                    <div>
                        <h4 className="text-md font-semibold text-accent-800 mb-4 pb-2 border-b">Employment Information</h4>
                        <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Household Size</label>
                                    <input
                                        type="number"
                                        value={enrollmentData.householdSize || 1}
                                        readOnly
                                        className="w-full px-3 py-2 border border-gray-300 bg-gray-100 rounded-md cursor-not-allowed"
                                    />
                                    <p className="mt-1 text-xs text-gray-500">Auto-calculated from Step 2 (Spouse and Dependents)</p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Employment Type <span className="text-red-500">*</span></label>
                                    <select
                                        value={enrollmentData.employmentType || ''}
                                        onChange={(e) => updateEnrollmentData('employmentType', e.target.value)}
                                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.employmentType ? 'border-red-500' : 'border-gray-300'}`}
                                    >
                                        <option value="">Please Select</option>
                                        <option value="Employed">Employed</option>
                                        <option value="Self-Employed">Self-Employed</option>
                                    </select>
                                    {errors.employmentType && <p className="mt-1 text-sm text-red-600">{errors.employmentType}</p>}
                                </div>
                            </div>

                            <div className="flex flex-col gap-1">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Client Annual Income <span className="text-red-500">*</span></label>
                                <div className="flex gap-3 items-start">
                                    <div className="flex-1 max-w-sm">
                                        <div className="relative">
                                            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                                <span className="text-gray-500 sm:text-sm">$</span>
                                            </div>
                                            <input
                                                type="number"
                                                placeholder="0.00"
                                                value={enrollmentData.annualIncome || ''}
                                                onChange={(e) => updateEnrollmentData('annualIncome', e.target.value)}
                                                onBlur={(e) => {
                                                    const val = parseFloat(e.target.value)
                                                    if (!isNaN(val)) {
                                                        const rounded = Math.floor(val / 100) * 100
                                                        updateEnrollmentData('annualIncome', rounded.toString())
                                                    }
                                                }}
                                                className={`w-full pl-7 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.annualIncome ? 'border-red-500' : 'border-gray-300'}`}
                                            />
                                        </div>
                                        <p className="mt-1 text-xs text-gray-500">Includes Spouse Income If "Married File Joint"</p>
                                        {errors.annualIncome && <p className="mt-1 text-sm text-red-600">{errors.annualIncome}</p>}
                                    </div>

                                    <button
                                        type="button"
                                        onClick={suggestIncome}
                                        className="h-10 px-3 mt-0 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-md hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 flex items-center justify-center gap-1.5 font-bold text-[10px] uppercase tracking-wider shadow-sm shrink-0"
                                    >
                                        <Sparkles className="w-3 h-3" />
                                        AUTO
                                    </button>
                                </div>
                            </div>

                            {enrollmentData.employmentType === 'Employed' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Employer's Name</label>
                                    <input
                                        type="text"
                                        value={enrollmentData.employerName || ''}
                                        onChange={(e) => updateEnrollmentData('employerName', e.target.value)}
                                        placeholder="Company Name"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            )}

                            {/* Work Type - Visible in all views when Self-Employed is selected */}
                            {(enrollmentData.employmentType === 'Self-Employed' || revealAllFields) && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Work Type <span className="text-red-500">*</span></label>
                                    <select
                                        value={enrollmentData.typeOfWork || ''}
                                        onChange={(e) => updateEnrollmentData('typeOfWork', e.target.value)}
                                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.typeOfWork ? 'border-red-500' : 'border-gray-300'}`}
                                    >
                                        {SELF_EMPLOYED_WORK_TYPES.map(type => (
                                            <option key={type} value={type === 'Please Select' ? '' : type}>
                                                {type}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.typeOfWork && <p className="mt-1 text-sm text-red-600">{errors.typeOfWork}</p>}
                                </div>
                            )}

                            {/* Medicaid Warning - Income Too Low */}
                            {medicaidWarning && (
                                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-md">
                                    <div className="flex">
                                        <div className="flex-shrink-0">
                                            <AlertTriangle className="h-5 w-5 text-yellow-400" />
                                        </div>
                                        <div className="ml-3">
                                            <h3 className="text-sm font-medium text-yellow-800">Medicaid Eligibility Warning</h3>
                                            <div className="mt-2 text-sm text-yellow-700">
                                                <p>
                                                    Based on household size of <strong>{enrollmentData.householdSize}</strong> and income of <strong>${parseFloat(enrollmentData.annualIncome || 0).toLocaleString()}</strong>,
                                                    client may qualify for Medicaid instead.
                                                </p>
                                                <p className="mt-2">
                                                    Income is <strong>{medicaidWarning.percentage}% of FPL</strong>, below 138% FPL threshold (${medicaidWarning.threshold.toLocaleString()}).
                                                </p>
                                                <p className="mt-2 font-semibold">
                                                    Client may be disqualified from ACA and should apply for Medicaid.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* High Income Warning */}
                            {highIncomeWarning && (
                                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-md">
                                    <div className="flex">
                                        <div className="flex-shrink-0">
                                            <Info className="h-5 w-5 text-blue-400" />
                                        </div>
                                        <div className="ml-3">
                                            <h3 className="text-sm font-medium text-blue-800">No Subsidy Eligibility</h3>
                                            <div className="mt-2 text-sm text-blue-700">
                                                <p>
                                                    Income is <strong>{highIncomeWarning.percentage}% of FPL</strong>, above 400% FPL threshold (${highIncomeWarning.threshold.toLocaleString()}).
                                                </p>
                                                <p className="mt-2 font-semibold">
                                                    Client will pay full price without government subsidies.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* SECTION: Health Insurance Plan */}
                    <div>
                        <h4 className="text-md font-semibold text-accent-800 mb-4 pb-2 border-b">Health Insurance Plan Selection</h4>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Preferred Carrier <span className="text-red-500">*</span>
                                </label>
                                {availableCarriers.length > 0 ? (
                                    <select
                                        value={enrollmentData.selectedCarrier || ''}
                                        onChange={(e) => {
                                            const selectedCode = e.target.value
                                            const selectedCarrier = availableCarriers.find((c: any) => c.code === selectedCode)
                                            updateEnrollmentData('selectedCarrier', selectedCode)
                                            // Use carrier NAME for carrierDesired (not code) - required for JotForm mapping
                                            updateEnrollmentData('carrierDesired', selectedCarrier?.name || selectedCode)
                                        }}
                                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.selectedCarrier ? 'border-red-500' : 'border-gray-300'}`}
                                    >
                                        <option value="">Select a Carrier</option>
                                        {availableCarriers.map((carrier: any) => (
                                            <option key={carrier.id} value={carrier.code}>
                                                {carrier.name} {carrier.isPreferred ? '★ (Preferred)' : ''} {carrier.commissionRate !== null && (isMasterMode || user?.role === 'fmo_ceo' || user?.role === 'sales_manager' || user?.role === 'admin') ? `($${carrier.commissionRate} commission)` : ''}
                                            </option>
                                        ))}
                                    </select>
                                ) : (
                                    <>
                                        <input
                                            type="text"
                                            value={enrollmentData.carrierDesired || ''}
                                            onChange={(e) => updateEnrollmentData('carrierDesired', e.target.value)}
                                            placeholder="e.g. Blue Cross, Aetna, etc."
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                        <p className="mt-1 text-xs text-gray-500">
                                            No carriers loaded from verification. You can type a carrier preference.
                                        </p>
                                    </>
                                )}
                                {errors.selectedCarrier && <p className="mt-1 text-sm text-red-600">{errors.selectedCarrier}</p>}
                            </div>

                            {/* Show available carriers from state */}
                            {availableCarriers.length > 0 && (
                                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                                    <h5 className="font-semibold text-green-800 mb-2">Available Carriers for {enrollmentData.clientState}:</h5>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                        {availableCarriers.map((carrier: any) => (
                                            <div
                                                key={carrier.id}
                                                className={`p-2 rounded border text-sm cursor-pointer transition-all ${enrollmentData.selectedCarrier === carrier.code
                                                    ? 'bg-green-200 border-green-500 font-semibold'
                                                    : 'bg-white border-green-100 hover:border-green-300'
                                                    }`}
                                                onClick={() => {
                                                    updateEnrollmentData('selectedCarrier', carrier.code)
                                                    updateEnrollmentData('carrierDesired', carrier.name)
                                                }}
                                            >
                                                <span className="text-gray-800">{carrier.name}</span>
                                                {carrier.isPreferred && (
                                                    <Star className="w-3 h-3 fill-amber-500 text-amber-500 inline ml-1" />
                                                )}
                                                {carrier.commissionRate !== null && (isMasterMode || user?.role === 'fmo_ceo' || user?.role === 'sales_manager' || user?.role === 'admin') && (
                                                    <span className="block text-green-600 text-xs">${carrier.commissionRate}</span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Customer Notes
                                </label>
                                <textarea
                                    rows={4}
                                    value={enrollmentData.customerNotes || ''}
                                    onChange={(e) => updateEnrollmentData('customerNotes', e.target.value)}
                                    placeholder="Any special needs or notes about the client's health plan preferences..."
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </StageWrapper>
    )
}
